import { customElement, property, state } from 'lit/decorators.js';
import { html, LitElement, PropertyValues } from 'lit';
import { dataBroker, NO_VALUE_OR_NO_SUBSCRIPTION_YET } from '../services/DataBroker';
import Debug from 'debug';
import { lastValueFrom } from 'rxjs';

interface DatasourceStats {
  name: string
  status: string
  updates: number
}

const log = Debug('pdq:ui:databroker');

@customElement('pdq-databroker')
export class PdqDatabroker extends LitElement {
  @state() forceRedraw = 0

  firstUpdated(_changedProperties: PropertyValues) {

    log('firstUpdated', dataBroker.sourcesKeys)
    dataBroker.sourcesKeys.forEach(this.boundSourceAdded)
    dataBroker.dataSourceAdded.subscribe(this.boundSourceAdded)
    dataBroker.dataSourceRemoved.subscribe((source) => {
      log('source removed', source)
      this.forceRedraw++
      this.requestUpdate()
    })
    dataBroker.dataSourcesChanged.subscribe((sources) => {
      log('sources changed', sources)
      this.forceRedraw++
      this.requestUpdate()
    })
  }

  boundSourceAdded = this.sourceAdded.bind(this)
  async sourceAdded(source: string) {
    log('source added', source)
    this.forceRedraw++
    this.requestUpdate()

    const ds = await dataBroker.getObservableDataSource(source)
    ds.subscribe((data:any) => {
      log(`source ${source} updated`, data)
      this.forceRedraw++
      this.requestUpdate()
    })
  }

  render() {
    log('render');
    return html`
      <cds-table size="sm">
        <cds-table-head>
          <cds-table-header-row>
            <cds-table-header-cell>Datasource</cds-table-header-cell>
            <cds-table-header-cell>Changes</cds-table-header-cell>
            <cds-table-header-cell>Actions</cds-table-header-cell>
          </cds-table-header-row>
        </cds-table-head>
        <cds-table-body>
          ${
            dataBroker.sourcesKeys.map((source) => {
              const ds = dataBroker._getDataSourceMeta(source)!
              
              return html`
                <cds-table-row>
                  <cds-table-cell>${source}</cds-table-cell>
                  <cds-table-cell>${ds?.changeCount}</cds-table-cell>
                  <cds-table-cell>
                    <cds-button @click=${() => {this.emmit(source, ds)}} size="sm" kind="secondary">Emmit to console</cds-button>
                    <cds-button @click=${() => {this.copy(source, ds)}} size="sm" kind="secondary">Copy to clipboard</cds-button>
                    <cds-button @click=${() => {this.download(source, ds)}} size="sm" kind="secondary">Download</cds-button>
                    <cds-button @click=${(evt:any) => { evt.target.parentNode?.querySelector('input')?.click() }} size="sm" kind="secondary">Upload</cds-button>
                    <input type="file" data-source="${source}" style="display: none" @change="${this.upload}" />
                  </cds-table-cell>
                </cds-table-row>
              `
            })
          }
        </cds-table-body>
      </cds-table> 
    `
  }

  copy(source:string, ds: {lastValue: any} ) {
    if (!ds.lastValue) {
      navigator.clipboard.writeText(ds.lastValue)
    }
    else if (ds.lastValue === NO_VALUE_OR_NO_SUBSCRIPTION_YET) {
      navigator.clipboard.writeText('NO_VALUE_OR_NO_SUBSCRIPTION_YET')
    }
    else if (typeof ds.lastValue === 'string') {
      navigator.clipboard.writeText(ds.lastValue)
    }
    else if (ds.lastValue instanceof Blob) {
      const clipboardItem = new ClipboardItem({
        [ds.lastValue.type]: ds.lastValue
      });
      navigator.clipboard.write([clipboardItem]);
    }
    else {
      navigator.clipboard.writeText(JSON.stringify(ds.lastValue, null, 2));
    }
  }

  emmit(source:string, ds: {lastValue: any}) {
    if (!ds.lastValue) {
      console.log(`Source: ${source} with type ${typeof ds.lastValue} has falsy value '${ds.lastValue}'`)
    }
    else if (ds.lastValue === NO_VALUE_OR_NO_SUBSCRIPTION_YET) {
      console.log(`Source: ${source} has no value, or no subscribers yet`)
    }
    else if (typeof ds.lastValue === 'string') {
      console.log(`Source: ${source} with type ${typeof ds.lastValue}`)
      console.log(ds.lastValue);
    }
    else if (ds.lastValue instanceof Blob) {
      console.log(`Source: ${source} is a Blob`)
      console.log(ds.lastValue);
    }
    else {
      console.log(`Source: ${source} has type ${typeof ds.lastValue} and serializes to... `)
      console.log(JSON.stringify(ds.lastValue, null, 2));
    }

  }

  download(source:string, ds: {lastValue: any}) {
    if (!ds.lastValue) {
      console.log(`Source: ${source} with type ${typeof ds.lastValue} has falsy value '${ds.lastValue}'`)
      return
    }
    if (ds.lastValue === NO_VALUE_OR_NO_SUBSCRIPTION_YET) {
      console.log(`Source: ${source} has no value, or no subscribers yet`)
      return;
    }

    let blob, filename
    if (typeof ds.lastValue === 'string') {
      console.log(`Source: ${source} with type ${typeof ds.lastValue}`)
      blob = new Blob([ds.lastValue], { type: 'text/plain' });
      filename = `${source}.txt`
    }
    else if (ds.lastValue instanceof Blob) {
      console.log(`Source: ${source} is a Blob`)
      blob = ds.lastValue
      filename = `${source}.getFileExtension(ds.lastValue)}`
    }
    else {
      console.log(`Source: ${source} has type ${typeof ds.lastValue} and serializes to... `)
      blob = new Blob([JSON.stringify(ds.lastValue, null, 2)], { type: 'application/json' });
      filename = `${source}.json`
    }


    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    window.URL.revokeObjectURL(url);
  }

  upload (evt: InputEvent) {
    const file = (evt.target as HTMLInputElement).files![0];
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result;
      console.log(content);
    };
    reader.readAsText(file);

  }

}

function getFileExtension(blob: Blob) {
  const mimeTypeMap: any = {
    // Data Formats
    'text/csv': 'csv',
    'application/csv': 'csv',
    'application/vnd.ms-excel.csv': 'csv',
    'application/xml': 'xml',
    'text/xml': 'xml',
    'application/vnd.sqlite3': 'sqlite',
    'application/x-sqlite3': 'sqlite',
    'application/vnd.apache-arrow': 'arrow',
    'application/x-apache-arrow': 'arrow',

    // Images
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/svg+xml': 'svg',

    // Documents
    'application/pdf': 'pdf',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.ms-powerpoint': 'ppt',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'pptx',

    // Text
    'text/plain': 'txt',
    'text/html': 'html',
    'text/css': 'css',
    'text/javascript': 'js',

    // Audio
    'audio/mpeg': 'mp3',
    'audio/wav': 'wav',
    'audio/ogg': 'ogg',

    // Video
    'video/mp4': 'mp4',
    'video/webm': 'webm',
    'video/ogg': 'ogv',

    // Archives
    'application/zip': 'zip',
    'application/x-rar-compressed': 'rar',
    'application/x-7z-compressed': '7z'
  };
  const type = blob.type.toLowerCase();
  return type in mimeTypeMap ? mimeTypeMap[type] : 'unknown';
}